<template>
  <v-dialog
    v-model="dialog"
    max-width="1200"
  >
    <v-card>
      <v-card-title class="text-h5 z-card-title">
        <v-icon left>mdi-truck-fast-outline</v-icon>
        {{ title }}
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="mt-5">
        <v-autocomplete
          ref="autocompleteRef"
          v-model="selectedAddress"
          :items="addressSuggestions"
          :no-filter="true"
          :label="$t('Customer.enter_new_address')"
          :search-input.sync="searchText"
          item-text="label"
          :return-object="true"
          :hide-no-data="true"
          :loading="isLoading"
          :no-data-text="$t('Common.no_data')"
        ></v-autocomplete>

        <MapLibre
          v-if="isVisible"
          :markers="selectedCoordinates ? [selectedCoordinates] : []"
        ></MapLibre>

        <div v-if="alertText" class="alert-container mt-5">
          <v-alert dense type="error">{{ alertText }}</v-alert>
        </div>

        <v-card-actions class="d-flex justify-center">
          <v-btn
            class="mx-0"
            color="zwonogreen"
            :loading="isSaving"
            :disabled="!foundLocation"
            @click="saveBtnClik"
          >
            {{ $t("Common.select") }}
          </v-btn>

          <v-btn
            class="mx-0 ml-5"
            color="warning"
            :loading="isSaving"
            @click="cancelBtnClick"
          >
            {{ $t("Common.cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import MapLibre from "@/components/maplibre/MapLibre.vue";
import { mapGetters } from "vuex";
import {
  fetchAddressSuggestionsPlanPlusV2,
  fetchLocationDetailsPlanPlusV2,
  fetchStreetDetailsPlanPlusV2
} from "../../helpers/address-search-helpers.js";

export default {
  components: {
    MapLibre,
  },
  props: {
    isVisible: { type: Boolean, default: false, required: true },
    isSaving: { type: Boolean, default: false, required: true },
    searchAddressApi: { type: String, default: "", required: false },
    title: { type: String, required: true },
    prefill: { type: String, default: null, required: false },
  },
  data() {
    return {
      dialog: this.isVisible,
      searchText: null,
      selectedAddress: null,
      selectedCoordinates: null,
      foundLocation: null,
      addressSuggestions: [],
      isLoading: false,
      alertText: "",
    };
  },
  computed: {
    ...mapGetters(["getSearchAddressApi"]),
  },
  watch: {
    isVisible(value) {
      this.dialog = value;
      if (this.isVisible && this.prefill) {
        this.fetchAddressSuggestions(this.prefill);
      }
    },
    dialog(value) {
      this.$emit("is-visible", value);
    },
    searchText(text) {
      if (this.isVisible && text) {
        this.fetchAddressSuggestions(text);
      }
    },
    async selectedAddress(value) {
      if (!value || !value?.value?.token) return;

      this.isLoading = true;

      try {
        // If result is a street, resolve via street-v2 with a houseNumber extracted from the text/title
        if (value.value.type === 'street') {
          const house = this.extractHouseNumber(
            value.value.title || value.value.address || this.searchText
          );

          const details = await fetchStreetDetailsPlanPlusV2(value.value.token, house);

          if (!details) {
            this.alertText = 'Failed to fetch street details';
            this.selectedCoordinates = null;
            this.foundLocation = {
              apiType: 'Planplus',
              address1: value.value.title || value.value.address,
              city: value.value.title || value.value.address,
              country: 'RS',
            };
            return;
          }

          this.foundLocation = {
            apiType: 'Planplus',
            country: details.country || 'RS',
            city: details.place || details.municipality || '',
            postal_code: details.postalCode || null,
            address1: details.street || '',
            house_number: details.houseNumber || house || '',
            appartment_details: details.name || '',
            lat: details.lat || null,
            lon: details.lng || null,
          };

          if (!details.lat || !details.lng) {
            this.alertText = 'Location found but coordinates missing. Please select location on map.';
            this.selectedCoordinates = null;
          } else {
            this.selectedCoordinates = { lat: details.lat, lon: details.lng };
            this.alertText = '';
          }

          return; // stop here for street flow
        }

        // Non-street (place/object): resolve via location-details-v2 (object)
        const details = await fetchLocationDetailsPlanPlusV2(value.value.token);

        if (!details) {
          this.alertText = 'Failed to fetch location details';
          this.foundLocation = {
            apiType: 'Planplus',
            address1: value.value.title || value.value.address,
            city: value.value.title || value.value.address,
            country: 'RS',
          };
          this.selectedCoordinates = null;
          return;
        }

        this.foundLocation = {
          apiType: 'Planplus',
          country: details.country || 'Republika Srbija' || 'RS',
          city: details.place || details.municipality || '',
          postal_code: details.postalCode || null,
          address1: details.street || '',
          house_number: details.houseNumber || '',
          appartment_details: details.name || '',
          lat: details.lat || null,
          lon: details.lng || null,
        };

        if (!details.lat || !details.lng) {
          this.alertText = 'Location found but coordinates missing. Please select location on map.';
          this.selectedCoordinates = null;
        } else {
          this.selectedCoordinates = { lat: details.lat, lon: details.lng };
          this.alertText = '';
        }
      } catch (error) {
        this.alertText = 'Error fetching location details';
        this.selectedCoordinates = null;
        this.foundLocation = {
          apiType: 'Planplus',
          address1: value?.value?.title || value?.value?.address,
          city: value?.value?.title || value?.value?.address,
          country: 'RS',
        };
      } finally {
        this.isLoading = false;
      }
    },
  },
  methods: {
    extractHouseNumber(str) {
      const m = String(str || '').match(/\b(\d+[A-Za-z]?)\b/);
      return m ? m[1] : '';
    },

    async fetchAddressSuggestions(searchText) {
      if (!searchText || searchText.trim().length < 2) {
        this.addressSuggestions = [];
        return;
      }

      this.isLoading = true;

      try {
        // Optionally pass known coords (e.g., current marker) to improve ranking
        const coords = this.selectedCoordinates
          ? { lat: this.selectedCoordinates.lat, lng: this.selectedCoordinates.lon }
          : null;

        const results = await fetchAddressSuggestionsPlanPlusV2(searchText, coords);

        this.addressSuggestions = results.map((result) => ({
          label: result.address || result.title,
          value: {
            ...result,
            apiType: 'Planplus',
          },
        }));
      } catch (error) {
        this.addressSuggestions = [];
        this.alertText = 'Error fetching address suggestions';
      } finally {
        this.isLoading = false;
      }
    },

    async saveBtnClik() {
      const addressObj = {
        country: this.foundLocation?.country || 'RS',
        city: this.foundLocation?.city || null,
        postal_code: this.foundLocation?.postal_code || null,
        address1: this.foundLocation?.address1 || null,
        house_number: this.foundLocation?.house_number || null,
        appartment_details: this.foundLocation?.appartment_details || null,
        lat: this.selectedCoordinates?.lat || this.foundLocation?.lat || null,
        lon: this.selectedCoordinates?.lon || this.foundLocation?.lon || null,
      };

      this.$emit("address-object", addressObj);

      setTimeout(() => {
        this.closeDialog();
      }, 100);
    },

    cancelBtnClick() {
      this.closeDialog();
    },

    closeDialog() {
      this.dialog = false;
      this.searchText = null;
      this.selectedAddress = null;
      this.selectedCoordinates = null;
      this.foundLocation = null;
      this.addressSuggestions = [];
      this.isLoading = false;
      this.alertText = "";
    },
  },
};
</script>